import {Events, NavController} from "ionic-angular";
import {Injectable, NgZone} from "@angular/core";
import {
  AuthenticationProvider,
  AuthenticationProviderEvent,
  CheckUtils,
  CurrentPageVoData,
  CustomerAccessPolicyVoData,
  CustomerAppProvider,
  CustomerAppProviderEvent,
  CustomerAppTypesVo,
  CustomerAppVoData,
  ICustomerApps,
  JabberProvider,
  MessengerProvider,
  MessengerProviderEvent,
  OrganizationDetailsProvider,
  OrganizationDetailsProviderEvent,
  OrganizationProvider,
  PagesProvider
} from "od-ts"
import {NavOptions, Page, TransitionDoneFn} from "ionic-angular/navigation/nav-util";

@Injectable()
export class AppRouter {
  private zone: NgZone;
  private _refresh: boolean;
  private timer;
  private nav: NavController;
  private _componentName: string;
  private _canViewAnonymously: Boolean;
  private _customerPortalVisibilityWidget: Boolean;

  private _changedHandler: () => void;
  private _mainLoadedHandler: () => void;
  private _onPageLoadedHandler: (res: CurrentPageVoData) => any;
  private _organizationDetailsProviderHandler: () => any;
  private _pageHandler: () => any;
  private _data: any;
  private _lastPageVoData: CurrentPageVoData;

  constructor(private orgProvider: OrganizationProvider,
              private organizationDetailsProvider: OrganizationDetailsProvider,
              private pagesProvider: PagesProvider,
              private authProvider: AuthenticationProvider,
              private messengerProvider: MessengerProvider,
              private _customerAppProvider: CustomerAppProvider,
              private jabberProvider: JabberProvider,
              private events: Events) {

    this._customerPortalVisibilityWidget = null;
    this._canViewAnonymously = null;
    this.setup();
  }

  private setup() {
    this.zone = new NgZone({enableLongStackTrace: false});
    this._onPageLoadedHandler = (res: CurrentPageVoData) => {
      this._lastPageVoData = res;
    };
    this._changedHandler = () => {
      this.router(this._componentName, this._data);
    };

    this._mainLoadedHandler = () => this.zone.run(() => {
      if (this.messengerProvider.ready && this._customerAppProvider.ready) {
        this.router(this._componentName, this._data);
      }
    });

    this._organizationDetailsProviderHandler = () => {
      if (this.organizationDetailsProvider.ready) {
        this._canViewAnonymously = this.organizationDetailsProvider.model.canViewAnonymously;
        this._changedHandler();
      }
    };

    this._pageHandler = () => {
      if (this.pagesProvider.model.page != null) {
        clearTimeout(this.timer);
        if (!CheckUtils.isEmpty(this._componentName)) {
          this.navTo(this.pagesProvider.model.page.name, this.pagesProvider.model.page.data, true);
        }
      }
    };

  }

  private bind() {
    this.events.subscribe(this.pagesProvider.model.event.PAGE_LOADED, this._onPageLoadedHandler);
    this.events.subscribe(OrganizationDetailsProviderEvent.READY, this._organizationDetailsProviderHandler);
    this.events.subscribe(MessengerProviderEvent.READY, this._mainLoadedHandler);
    this.events.subscribe(CustomerAppProviderEvent.READY, this._mainLoadedHandler);
    this.events.subscribe(this.pagesProvider.model.event.PAGE_LOADED, this._pageHandler);
    this.events.subscribe(AuthenticationProviderEvent.SIGNED, () => this._changedHandler());
  }

  init(nav: NavController) {
    this.nav = nav;
    this.bind();
    let a = window.location.hash.split("/");

    this.pagesProvider.model.load(a[1]);
  }

  private router(componentName: any, data = {
    organization: this.orgProvider.model.uri,
    context: this.pagesProvider.model.context,
    id: null
  }) {
    if (!CheckUtils.areEmpty(this.nav, componentName) && (CheckUtils.isEmpty(this.nav.getActive()) || this.nav.getActive().component.name != componentName ||
      ((!this.canNavTo(componentName, data.id) || componentName === "CustomerPortalPage") && this.nav.getActive().component.name == componentName))) {
      if (CheckUtils.isEmpty(this.nav.getActive()) && !CheckUtils.isEmpty(this._lastPageVoData) &&
        this._lastPageVoData.name === componentName && !CheckUtils.isEmpty(this._lastPageVoData.data)) {
        data = this._lastPageVoData.data;
      }
      this.push(componentName, data, null, null, 800);
    }
  }

  private canNavTo(componentName: string, id?: number) {
    let canNav: boolean = true;
    let messenger: CustomerAppVoData | undefined;
    switch (this._componentName) {
      case 'ProfilePage':
        canNav = this.authProvider.isAuthenticated();
        break;
      case 'SignInPage':
      case 'SignUpPage':
      case 'RegisterPage':
      case 'ResetPasswordPage':
        canNav = !this.authProvider.isAuthenticated();
        break;
      case 'ConversationsPage':
        messenger = this._customerAppProvider.getOneByType(CustomerAppTypesVo.MESSENGER);
        canNav = messenger !== undefined && messenger.activated === true;
        break;
      case 'MessagesPage':
        messenger = this._customerAppProvider.getOneByType(CustomerAppTypesVo.MESSENGER);
        canNav = messenger !== undefined && messenger.activated === true;
        break;
      case 'CustomerPortalPage':
      case 'OtherAppPage':
        if (id == null || !this._customerAppProvider.model.has(Number(id))) canNav = false;
        else {
          let app: CustomerAppVoData = this._customerAppProvider.model.get(Number(id));

          canNav = app.activated === true && (this.authProvider.isAuthenticated() ||
            app.accessPolicy == CustomerAccessPolicyVoData.ANONYMOUS_AND_LOGIN);
        }
        break;
      case 'WelcomePage':
        messenger = this._customerAppProvider.getOneByType(CustomerAppTypesVo.MESSENGER);
        canNav = messenger !== undefined && messenger.activated === true && !this.messengerProvider.model.welcomeVisited;
        break;
    }
    return canNav;
  }

  navTo(componentName: string, data?: any, restore?: boolean): boolean {

    if (CheckUtils.isEmpty(componentName)) return false;

    if (CheckUtils.isEmpty(this._componentName) && !CheckUtils.isEmpty(this.pagesProvider.model.page)) {
      this._componentName = this.pagesProvider.model.page.name;
      this._data = this.pagesProvider.model.page.data;
    } else {
      this._componentName = componentName;
      this._data = data;
    }

    this.setContext(data, restore);
    this.orgProvider.setUri(data.organization);

    if (!this.messengerProvider.ready || !this._customerAppProvider.ready) return false;

    if (this._componentName === "PageNotFoundPage") return true;

    if ((CheckUtils.isEmpty(data) || CheckUtils.isEmpty(data.organization))) {
      this.redirection('PageNotFoundPage');
      return false;
    }

    let canNav: boolean = this.canNavTo(componentName, data.id);

    if (canNav && !document.body.classList.contains("app-ready")) {
      document.body.classList.add("app-ready");
    }
    let messenger: CustomerAppVoData | undefined;
    switch (this._componentName) {
      case 'ProfilePage':
        if (!canNav) this.redirection('SignInPage');
        break;
      case 'SignInPage':
      case 'SignUpPage':
      case 'ResetPasswordPage':
        if (!canNav) {
          if (CheckUtils.isEmpty(data.from))
            this.redirection('ConversationsPage');
          else if (CheckUtils.isEmpty(data.fromId))
            this.redirection(data.from, 100);
          else
            this.redirection(data.from, 100, {id: data.fromId});
        }
        break;
      case 'RegisterPage':
        if (!canNav) this.redirection('SignUpPage');
        break;
      case 'ConversationsPage':
        messenger = this._customerAppProvider.getOneByType(CustomerAppTypesVo.MESSENGER);
        if (!canNav) this.redirection('CustomerPortalPage', messenger !== undefined ? 2000 : 100);
        break;
      case 'MessagesPage':
        messenger = this._customerAppProvider.getOneByType(CustomerAppTypesVo.MESSENGER);
        if (!canNav) this.redirection('CustomerPortalPage', messenger !== undefined ? 2000 : 100);

        if (this._data.id === ":id") this._data.id = null;

        if (!CheckUtils.isEmpty(this._lastPageVoData) && 'MessagesPage' === this._lastPageVoData.name &&
          !CheckUtils.isEmpty(this._lastPageVoData.data.id) && this._data.id == null) {
          this._data.id = this._lastPageVoData.data;
          this._data._isNewConversation = false;
        } else if (CheckUtils.isEmpty(this._data.id)) {
          this._data._isNewConversation = true;
          this._data.id = this.jabberProvider.getNewConversationJabberId();
        }

        break;
      case 'OtherAppPage':
      case 'CustomerPortalPage':
        if (data.id != null && this._customerAppProvider.model.has(data.id) &&
          this.authProvider.isAuthenticated() === false &&
          this._customerAppProvider.model.get(data.id).accessPolicy == CustomerAccessPolicyVoData.LOGIN) {
          this.redirection('SignInPage', this._customerPortalVisibilityWidget == null ? 2000 : 100,
            {from: this._componentName, id: data.id});
        } else if (!canNav || data.id == null || !this._customerAppProvider.model.has(data.id) ||
          !this._customerAppProvider.model.get(data.id).activated) {
          if (!this._customerAppProvider.model.isEmpty()) {
            let customerApps: Array<ICustomerApps> = [];
            this._customerAppProvider.model.getAll().forEach((customerApp) => customerApps.push(customerApp));

            customerApps.sort((o1: ICustomerApps, o2: ICustomerApps) => {
              if (o1.position == null) {
                return -1;
              } else if (o2.position == null) {
                return 1;
              } else if (o1.position < o2.position) {
                return -1;
              } else if (o1.position > o2.position) {
                return 1;
              }
              return 0;
            });

            if (this._data.id == null) {
              for (let customerApp of customerApps) {
                if (customerApp.activated && customerApp.type == CustomerAppTypesVo.CUSTOMER_PORTAL) {
                  this._data.id = customerApp.uid;
                  break;
                }
              }
            }
            if (data.id == null || !canNav) {
              this.redirection('SignInPage', this._customerPortalVisibilityWidget == null ? 2000 : 100,
                {from: this._componentName, fromId: data.id});
            } else if (this.nav.getActive().name != this._componentName && !canNav) {
              this.redirection(this._componentName, this._customerPortalVisibilityWidget == null ? 2000 : 100,
                {id: data.id});
            }
          }
        }
        break;
      case 'WelcomePage':
        if (!canNav) {
          let messenger = this._customerAppProvider.getOneByType(CustomerAppTypesVo.MESSENGER);
          if (messenger !== undefined && messenger.activated === true) {
            this.redirection('ConversationsPage', 0);
          } else {
            if (!canNav) this.redirection('CustomerPortalPage', messenger !== undefined ? 2000 : 100);
          }
        }
    }

    this.pagesProvider.setPage(new CurrentPageVoData(this._componentName, this._data));

    return canNav;
  }

  private redirection(componentName: string, delais = 100, params?: any) {
    this.push(componentName, params, null, null, delais);
  }

  push(page: Page | string, params?: any, opts?: NavOptions, done?: TransitionDoneFn, delais = 0) {
    if (this.timer != null) clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      this.nav.push(page, this.addOrgOnParams(params), opts, done).then(() => {
        if (!CheckUtils.isEmpty(this.nav.getActive()) && !CheckUtils.isEmpty(this.nav.getActive().index)) {
          var index: number = this.nav.getActive().index;
          if (index > 0) this.nav.remove(0, index);
        }
      }).catch(err => {
        console.log("Nav push rejected - " + err);
      });
    }, delais);

  }

  setRoot(page: Page | string, params?: any, opts?: NavOptions, done?: TransitionDoneFn) {
    this.nav.setRoot(page, this.addOrgOnParams(params), opts, done).then(() => {
    })
  }

  private addOrgOnParams(params: any): any {
    if (CheckUtils.isEmpty(params)) params = {
      organization: this.orgProvider.model.uri,
      context: this.pagesProvider.model.context,
      id: null
    };
    else {
      params.organization = this.orgProvider.model.uri;
      params.context = this.pagesProvider.model.context;
    }
    return params;
  }


  private setContext(data?: any, restore?: boolean) {
    if (data.context == ":context" || data.context == "undefined" || data.context == "null")
      this._data.context = ":context";
    if (restore) {
      data.context = this.pagesProvider.model.context;
    } else {
      this.pagesProvider.setContext(this._data.context);
    }
  }

  in(page: string) {
    return !CheckUtils.areEmpty(page, this.nav.getActive()) && page == this.nav.getActive().component.name;
  }


  get refresh(): boolean {
    return this._refresh;
  }

  set refresh(value: boolean) {
    this._refresh = value;
  }
}



// WEBPACK FOOTER //
// ./src/app/app.router.ts